import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { App } from 'antd'
import { ArrowLeft, User, Calendar, Clock } from 'lucide-react'
import { format } from 'date-fns'
import { InterviewService } from '../services'
import { InterviewInsightCard } from '../components/insights/InterviewInsightCard'
import { SkeletonSurveyInsights } from '../../../features/skeleton'
import {
  InsightsContainer,
  MetaRow,
  MetaBadge,
  Divider,
  QuestionsGrid,
} from '../../survey/pages/SurveyInsightsPageStyles'
import {
  PageHeader,
  BackButton,
  HeaderInfo,
  PageTitle,
  PageSubtitle,
  PRINT_STYLES,
} from '../../../styles/pageHeaderStyles'
import styled from 'styled-components'
import { SPACING } from '../../../styles/theme'
import type { InterviewNotesResponse } from '@bandos/shared'

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${SPACING.xxxl} ${SPACING.xl};
  color: var(--color-text-secondary);
`

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

export function InterviewTranscriptPage() {
  const { nodeId, noteId } = useParams<{ nodeId: string; noteId: string }>()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const [data, setData] = useState<InterviewNotesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isPrinting = false

  useEffect(() => {
    if (!nodeId) return

    void (async () => {
      setLoading(true)
      const result = await InterviewService.getNotes(nodeId)
      setLoading(false)

      if (result.error || !result.data) {
        const status = (result.error as { status?: number } | null)?.status
        if (status === 401 || status === 403) {
          notification.error({ title: 'Access denied' })
          void navigate('/')
          return
        }
        setError('Interview not found or you do not have access.')
        return
      }

      setData(result.data)
    })()
  }, [nodeId, navigate, notification])

  if (loading) {
    return <SkeletonSurveyInsights />
  }

  if (error || !data) {
    return (
      <InsightsContainer>
        <ErrorContainer>{error ?? 'Something went wrong.'}</ErrorContainer>
      </InsightsContainer>
    )
  }

  const note = data.notes.find((n) => n.id === noteId)

  if (!note) {
    return (
      <InsightsContainer>
        <ErrorContainer>Interview transcript not found.</ErrorContainer>
      </InsightsContainer>
    )
  }

  const name = note.intervieweeName?.trim() || 'Anonymous'
  const date = format(new Date(note.savedAt), 'MMM d, yyyy')
  const transcriptQuestions = note.questionsSnapshot ?? data.questions

  const noteIndex = data.notes.findIndex((n) => n.id === noteId)
  const interviewLabel = `Interview ${noteIndex + 1}${name !== 'Anonymous' ? ` — ${name}` : ''}`

  return (
    <InsightsContainer>
      <style>{PRINT_STYLES}</style>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)} title="Back to insights">
          <ArrowLeft size={18} />
        </BackButton>
        <HeaderInfo>
          <PageTitle>
            {data.parentLabel ? `${data.parentLabel}: ${interviewLabel}` : interviewLabel}
          </PageTitle>
          <PageSubtitle>Full transcript</PageSubtitle>
        </HeaderInfo>
      </PageHeader>

      <MetaRow>
        <MetaBadge>
          <User size={13} />
          {name}
        </MetaBadge>
        <MetaBadge>
          <Calendar size={13} />
          {date}
        </MetaBadge>
        {note.durationSeconds != null && note.durationSeconds > 0 && (
          <MetaBadge>
            <Clock size={13} />
            {formatDuration(note.durationSeconds)}
          </MetaBadge>
        )}
      </MetaRow>

      <Divider />

      <QuestionsGrid>
        {transcriptQuestions.map((question, i) => {
          const raw = note.answers[i]
          const answer = raw === null || raw === undefined ? '' : String(raw)
          return (
            <InterviewInsightCard
              key={question.id ?? i}
              question={question}
              questionIndex={i}
              answers={answer.length > 0 ? [answer] : []}
              isPrinting={isPrinting}
            />
          )
        })}
      </QuestionsGrid>
    </InsightsContainer>
  )
}
